import React, { useEffect, useRef, useState } from "react";
import {
  Activity,
  Users,
  UserPlus,
  Flame,
  CreditCard,
  Euro,
  RefreshCw,
  ArrowRight,
  ChevronDown,
  ChevronRight,
  ExternalLink,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  formPulseMetricsService,
  FormFunnelMetrics,
  FormTimeSeriesPoint,
  FormPulseEvent,
  FormCohortMatrix,
} from "@core/services/FormPulseMetricsService";

type Range = 1 | 5 | 10 | 20 | 30 | 90 | 180 | 360;
const RANGES: Range[] = [1, 5, 10, 20, 30, 90, 180, 360];

const formatNumber = (n: number) => n.toLocaleString("fr-FR");

const formatRelative = (iso: string): string => {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `il y a ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `il y a ${hours}h`;
  const days = Math.floor(hours / 24);
  return `il y a ${days}j`;
};

const maskEmail = (email: string | undefined): string => {
  if (!email || !email.includes("@")) return email ?? "—";
  const [local, domain] = email.split("@");
  const head = local.slice(0, 2);
  return `${head}${"•".repeat(Math.max(1, local.length - 2))}@${domain}`;
};

interface FunnelCardProps {
  icon: React.ReactNode;
  title: string;
  value: number;
  subtext: string;
  subtextTone?: "neutral" | "positive" | "negative";
  accent: string;
}

const FunnelCard: React.FC<FunnelCardProps> = ({
  icon,
  title,
  value,
  subtext,
  subtextTone = "neutral",
  accent,
}) => {
  const toneClass =
    subtextTone === "positive"
      ? "text-green-400"
      : subtextTone === "negative"
        ? "text-red-400"
        : "text-gray-400";
  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-6 flex-1 min-w-[180px]">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wide">
          {title}
        </h3>
        <div className={accent}>{icon}</div>
      </div>
      <div className="text-3xl font-bold text-white mb-1">
        {formatNumber(value)}
      </div>
      <div className={`text-xs ${toneClass}`}>{subtext}</div>
    </div>
  );
};

const ConversionArrow: React.FC<{ pct: number; label: string }> = ({
  pct,
  label,
}) => (
  <div className="flex flex-col items-center justify-center px-2 text-gray-500 min-w-[70px]">
    <ArrowRight className="w-4 h-4 mb-1" />
    <div className="text-sm font-semibold text-gray-300">{pct}%</div>
    <div className="text-[10px] text-gray-500">{label}</div>
  </div>
);

const EventRow: React.FC<{ event: FormPulseEvent }> = ({ event }) => {
  const [expanded, setExpanded] = useState(false);
  const { badge, color } = (() => {
    switch (event.type) {
      case "new_paying":
        return {
          badge: "Nouveau payant",
          color: "bg-green-500/20 text-green-300",
        };
      case "churn":
        return { badge: "Churn", color: "bg-red-500/20 text-red-300" };
      case "engaged_anon":
        return {
          badge: "Anonyme engagé",
          color: "bg-blue-500/20 text-blue-300",
        };
      case "first_poll":
        return {
          badge: "1er formulaire",
          color: "bg-purple-500/20 text-purple-300",
        };
    }
  })();

  const meta = event.meta as {
    plan?: string;
    email?: string;
    status?: string;
    polls_count?: number;
    fingerprint_prefix?: string;
    poll_type?: string;
    poll_title?: string;
    poll_slug?: string;
  };

  const hasDetails = !!meta.email || event.type === "engaged_anon";

  const detailContent = (() => {
    switch (event.type) {
      case "new_paying":
      case "churn":
        return (
          <>
            {meta.plan ?? "—"}
            {event.type === "churn" && meta.status
              ? ` · ${meta.status}`
              : ""} · {maskEmail(meta.email)}
          </>
        );
      case "engaged_anon":
        return (
          <>
            fp:{meta.fingerprint_prefix} · {meta.polls_count} formulaires
          </>
        );
      case "first_poll":
        return (
          <>
            {meta.poll_title ? `"${meta.poll_title.slice(0, 40)}"` : "—"} ·{" "}
            {maskEmail(meta.email)}
          </>
        );
    }
  })();

  return (
    <>
      <tr className="border-b border-white/5 hover:bg-white/5">
        <td className="px-4 py-2">
          <button
            onClick={() => hasDetails && setExpanded((v) => !v)}
            disabled={!hasDetails}
            className="flex items-center gap-1 disabled:cursor-default"
          >
            {hasDetails ? (
              expanded ? (
                <ChevronDown className="w-3 h-3 text-gray-500" />
              ) : (
                <ChevronRight className="w-3 h-3 text-gray-500" />
              )
            ) : (
              <span className="w-3 h-3" />
            )}
            <span className={`text-xs px-2 py-1 rounded ${color}`}>
              {badge}
            </span>
          </button>
        </td>
        <td className="px-4 py-2 text-sm text-white">{detailContent}</td>
        <td className="px-4 py-2 text-xs text-gray-500 text-right">
          {formatRelative(event.at)}
        </td>
      </tr>
      {expanded && hasDetails && (
        <tr className="border-b border-white/10 bg-black/30">
          <td colSpan={3} className="px-10 py-3 text-xs">
            {event.type === "first_poll" && meta.poll_slug && (
              <a
                href={`/poll/${meta.poll_slug}/vote`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 inline-flex items-center gap-1"
              >
                voir <ExternalLink className="w-3 h-3" />
              </a>
            )}
            {(event.type === "new_paying" || event.type === "churn") &&
              meta.email && (
                <div className="text-gray-300">
                  Email complet :{" "}
                  <span className="font-mono">{meta.email}</span>
                </div>
              )}
          </td>
        </tr>
      )}
    </>
  );
};

export const FormPulseDashboard: React.FC = () => {
  const [funnel, setFunnel] = useState<FormFunnelMetrics | null>(null);
  const [series, setSeries] = useState<FormTimeSeriesPoint[]>([]);
  const [events, setEvents] = useState<FormPulseEvent[]>([]);
  const [cohorts, setCohorts] = useState<FormCohortMatrix | null>(null);
  const [range, setRange] = useState<Range>(90);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async (r: Range) => {
    setLoading(true);
    setError(null);
    try {
      const [f, s, e, c] = await Promise.all([
        formPulseMetricsService.getFunnel(r),
        formPulseMetricsService.getTimeSeries(r),
        formPulseMetricsService.getRecentEvents(20),
        formPulseMetricsService.getQuarterlyCohorts(4),
      ]);
      setFunnel(f);
      setSeries(s);
      setEvents(e);
      setCohorts(c);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur inconnue");
    } finally {
      setLoading(false);
    }
  };

  // Chargement au mount uniquement, comme le Pulse global — les RPC sont
  // lourdes, on évite de les redéclencher à chaque changement de range.
  const hasLoadedRef = useRef(false);
  useEffect(() => {
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;
    load(90);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="bg-[#030712] text-white min-h-screen-safe">
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Activity className="w-6 h-6 text-blue-400" />
            <div>
              <h1 className="text-2xl font-semibold">Pulse — DooQuest</h1>
              <p className="text-sm text-gray-400">
                Santé acquisition formulaires · mise à jour{" "}
                {funnel ? formatRelative(funnel.generatedAt) : "…"}
              </p>
            </div>
          </div>
          <button
            onClick={() => load(range)}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10 text-white rounded-lg hover:bg-white/10 disabled:opacity-50 transition-colors text-sm"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Rafraîchir
          </button>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-300">
            {error}
          </div>
        )}

        <div className="mb-8 flex items-center justify-between bg-white/5 border border-white/10 rounded-xl p-3">
          <span className="text-xs uppercase tracking-wide text-gray-400 ml-2">
            Période (toute la page)
          </span>
          <div className="flex gap-1">
            {RANGES.map((r) => (
              <button
                key={r}
                onClick={() => setRange(r)}
                className={`px-3 py-1 text-xs rounded transition-colors ${
                  range === r
                    ? "bg-blue-500 text-white"
                    : "text-gray-400 hover:text-white hover:bg-white/10"
                }`}
              >
                {r}j
              </button>
            ))}
          </div>
        </div>

        {/* Bloc 1 — Funnel */}
        <section className="mb-10">
          <h2 className="text-sm uppercase tracking-wide text-gray-500 mb-4">
            Funnel — {range}j glissants
          </h2>
          <div className="flex flex-wrap items-stretch gap-2">
            <FunnelCard
              icon={<Users className="w-5 h-5" />}
              title={`Anonymes ${range}j`}
              value={funnel?.anonymous.current ?? 0}
              subtext={
                funnel
                  ? `${funnel.anonymous.deltaPct >= 0 ? "+" : ""}${funnel.anonymous.deltaPct}% vs ${range}j préc.`
                  : "—"
              }
              subtextTone={
                funnel
                  ? funnel.anonymous.deltaPct >= 0
                    ? "positive"
                    : "negative"
                  : "neutral"
              }
              accent="text-blue-400"
            />
            <ConversionArrow
              pct={funnel?.conversion.anonToSignup ?? 0}
              label="anon → 1er form"
            />
            <FunnelCard
              icon={<UserPlus className="w-5 h-5" />}
              title="Créateurs de forms"
              value={funnel?.signedUp.total ?? 0}
              subtext={
                funnel ? `+${funnel.signedUp.new30d} sur ${range}j` : "—"
              }
              subtextTone="positive"
              accent="text-purple-400"
            />
            <ConversionArrow
              pct={funnel?.conversion.signupToActive ?? 0}
              label="créateurs → actifs"
            />
            <FunnelCard
              icon={<Flame className="w-5 h-5" />}
              title={`Actifs ${range}j`}
              value={funnel?.active.count ?? 0}
              subtext={
                funnel ? `${funnel.active.pctOfSignedUp}% des créateurs` : "—"
              }
              accent="text-orange-400"
            />
            <ConversionArrow
              pct={funnel?.conversion.activeToPaying ?? 0}
              label="actifs → payants"
            />
            <FunnelCard
              icon={<CreditCard className="w-5 h-5" />}
              title="Pro (dooform_pro)"
              value={funnel?.paying.count ?? 0}
              subtext={
                funnel
                  ? `+${funnel.paying.new30d} / −${funnel.paying.churn30d} (${range}j)`
                  : "—"
              }
              subtextTone={
                funnel
                  ? funnel.paying.new30d >= funnel.paying.churn30d
                    ? "positive"
                    : "negative"
                  : "neutral"
              }
              accent="text-green-400"
            />
          </div>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-xl p-5">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wide">
                  MRR
                </h3>
                <Euro className="w-5 h-5 text-green-400" />
              </div>
              <div className="text-3xl font-bold text-white">
                {funnel
                  ? `${formatNumber(Math.round(funnel.paying.mrrEur))} €`
                  : "—"}
              </div>
              <div className="text-xs text-gray-400 mt-1">
                {funnel
                  ? `ARR ≈ ${formatNumber(Math.round(funnel.paying.mrrEur * 12))} €`
                  : ""}
              </div>
            </div>
          </div>
        </section>

        {/* Bloc 2 — Time series */}
        <section className="mb-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm uppercase tracking-wide text-gray-500">
              Tendance ({range}j)
            </h2>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl p-4 h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis
                  dataKey="date"
                  stroke="#9CA3AF"
                  style={{ fontSize: 11 }}
                  tickFormatter={(v) => v?.slice(5)}
                />
                <YAxis stroke="#9CA3AF" style={{ fontSize: 11 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#1F2937",
                    border: "1px solid #374151",
                    borderRadius: 8,
                    color: "white",
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line
                  type="monotone"
                  dataKey="anonymous"
                  name="Anonymes"
                  stroke="#60A5FA"
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="signed_up"
                  name="Créateurs"
                  stroke="#A78BFA"
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="active"
                  name="Actifs"
                  stroke="#FB923C"
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="paying"
                  name="Pro"
                  stroke="#34D399"
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>

        {/* Bloc Cohortes trimestrielles */}
        <section className="mb-10">
          <h2 className="text-sm uppercase tracking-wide text-gray-500 mb-2">
            Rétention par cohorte trimestrielle
          </h2>
          <p className="text-xs text-gray-500 mb-4">
            % de créateurs de la cohorte (1er form créé ce trimestre) encore
            actifs au trimestre N.
          </p>
          <div className="bg-white/5 border border-white/10 rounded-xl overflow-x-auto">
            {!cohorts || cohorts.cohorts.length === 0 ? (
              <div className="p-8 text-center text-gray-500 text-sm">
                Pas assez de données pour afficher les cohortes.
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10 bg-white/5 text-xs uppercase tracking-wide text-gray-400">
                    <th className="px-4 py-2 text-left font-medium">Cohorte</th>
                    <th className="px-4 py-2 text-right font-medium w-20">
                      Taille
                    </th>
                    {Array.from({ length: cohorts.max_offset + 1 }, (_, i) => (
                      <th
                        key={i}
                        className="px-4 py-2 text-center font-medium w-20"
                      >
                        {i === 0 ? "Q0 (1er form)" : `Q+${i}`}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {cohorts.cohorts.map((c) => (
                    <tr
                      key={c.cohort_quarter}
                      className="border-b border-white/5"
                    >
                      <td className="px-4 py-2 text-sm text-white font-mono">
                        {c.cohort_quarter}
                      </td>
                      <td className="px-4 py-2 text-right text-sm text-gray-300">
                        {c.size}
                      </td>
                      {c.retention.map((pt) => {
                        const pct =
                          c.size > 0 ? (pt.retained / c.size) * 100 : 0;
                        const bgOpacity = Math.min(pct / 100, 1) * 0.6;
                        const bg = `rgba(52, 211, 153, ${bgOpacity})`;
                        return (
                          <td
                            key={pt.q_offset}
                            className="px-4 py-2 text-center text-sm"
                            style={{ backgroundColor: bg }}
                            title={`${pt.retained}/${c.size}`}
                          >
                            <span className="font-semibold text-white">
                              {pt.retained}
                            </span>
                            <span className="text-gray-400 text-xs ml-1">
                              ({Math.round(pct)}%)
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>

        {/* Bloc 3 — Events */}
        <section className="mb-10">
          <h2 className="text-sm uppercase tracking-wide text-gray-500 mb-4">
            Derniers mouvements
          </h2>
          <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
            {events.length === 0 && !loading ? (
              <div className="p-8 text-center text-gray-500 text-sm">
                Aucun mouvement récent.
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10 bg-white/5">
                    <th className="px-4 py-2 text-left text-xs uppercase tracking-wide text-gray-400 font-medium w-40">
                      Type
                    </th>
                    <th className="px-4 py-2 text-left text-xs uppercase tracking-wide text-gray-400 font-medium">
                      Détail
                    </th>
                    <th className="px-4 py-2 text-right text-xs uppercase tracking-wide text-gray-400 font-medium w-32">
                      Quand
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((e, i) => (
                    <EventRow key={`${e.type}-${e.at}-${i}`} event={e} />
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      </main>
    </div>
  );
};

export default FormPulseDashboard;
